import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/admin/AdminGate";
import { SectionHeading } from "@/components/SectionHeading";
import { getTutorUsage } from "@/lib/api";
import { STUDY } from "@/constants/testIds";

const Container = ({ children }) => (
  <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">{children}</div>
);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

function CopyCode({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-[color:rgba(11,18,32,0.4)]">—</span>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      onClick={copy}
      title="Copy student reference"
      className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 font-mono text-xs hover:bg-[color:rgba(10,27,51,0.06)]"
    >
      {value}
      {copied ? <Check className="h-3.5 w-3.5 text-[#15803d]" /> : <Copy className="h-3.5 w-3.5 opacity-50" />}
    </button>
  );
}

function Bar({ value, cap }) {
  const pct = cap > 0 ? Math.min(100, Math.round((value / cap) * 100)) : 0;
  const color = pct >= 100 ? "#B42318" : pct >= 75 ? "#d97706" : "var(--ibp-gold-500)";
  return (
    <div className="flex items-center gap-2">
      <span className="min-w-[3.2rem] text-sm font-semibold text-[var(--ibp-ink)]">
        {value}
        <span className="text-[color:rgba(11,18,32,0.45)]"> / {cap}</span>
      </span>
      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[color:rgba(10,27,51,0.08)]">
        <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </span>
    </div>
  );
}

function UsageManager() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-tutor-usage"],
    queryFn: getTutorUsage,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-tutor-usage"] });

  const rows = useMemo(() => data?.rows || [], [data?.rows]);
  const dailyCap = data?.daily_cap ?? 20;
  const totalCap = data?.total_cap ?? 300;

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.today += r.today || 0;
        acc.total += r.total || 0;
        return acc;
      },
      { today: 0, total: 0 }
    );
  }, [rows]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium text-[color:rgba(11,18,32,0.6)]">
          {isLoading
            ? "Loading..."
            : `${rows.length} student(s) have used the tutor — ${totals.today} ask(s) today, ${totals.total} all-time.`}
        </p>
        <div className="flex items-center gap-2">
          <span
            className={[
              "rounded-full px-2.5 py-1 text-xs font-semibold",
              data?.enabled
                ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
                : "bg-[color:rgba(180,35,24,0.1)] text-[#B42318]",
            ].join(" ")}
          >
            {data ? (data.enabled ? "Tutor live" : "Tutor disabled (no key)") : "—"}
          </span>
          <button
            onClick={refresh}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-[color:rgba(10,27,51,0.2)] px-4 text-sm font-semibold text-[var(--ibp-ink)] hover:bg-[color:rgba(10,27,51,0.05)]"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-[var(--ibp-offwhite)] text-xs uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
            <tr>
              <th className="px-4 py-3">Student</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 min-w-[180px]">Today (cap {dailyCap})</th>
              <th className="px-4 py-3 min-w-[200px]">All-time (cap {totalCap})</th>
              <th className="px-4 py-3">Last used</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.student_ref}
                data-testid={STUDY.adminTutorUsageRow}
                className="border-t border-[color:rgba(10,27,51,0.08)]"
              >
                <td className="px-4 py-3">
                  <CopyCode value={r.student_ref} />
                </td>
                <td className="px-4 py-3 text-[color:rgba(11,18,32,0.8)]">{r.customer_email || "—"}</td>
                <td className="px-4 py-3">
                  <Bar value={r.today} cap={dailyCap} />
                </td>
                <td className="px-4 py-3">
                  <Bar value={r.total} cap={totalCap} />
                </td>
                <td className="px-4 py-3 text-xs text-[color:rgba(11,18,32,0.6)]">{fmtDate(r.last_used)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-[color:rgba(11,18,32,0.5)]">
                  No tutor usage yet. Counts appear here once students ask the AI tutor.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-[color:rgba(11,18,32,0.5)]">
        {`Caps are per student: ${dailyCap} asks/day and ${totalCap} all-time. A student who reaches a cap is asked to try again later.`}
      </p>
    </div>
  );
}

export default function AdminTutorUsage() {
  return (
    <div data-testid={STUDY.adminTutorUsagePage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-14">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Store admin"
            title="AI tutor usage"
            subtitle="Per-student tutor usage — today's asks and all-time totals against the caps."
          />
        </Container>
      </section>
      <section className="py-12 sm:py-16">
        <Container>
          <AdminGate>
            <UsageManager />
          </AdminGate>
        </Container>
      </section>
    </div>
  );
}
